/**
 * studioGuideContent — B-2026-0058, B-2026-0088
 *
 * Typed guide content registry for the Coaching Studio Adaptive Guidance Overlay.
 * All content is sport-agnostic and written for a coach audience — practical,
 * tool-specific.
 *
 * Context key priority (highest to lowest):
 *   focusedField > selectedTool > activePanel > activeSection(spy) > activePhase > 'studio-overview'
 *
 * B-2026-0088 changes:
 * - All `body` strings migrated to `StructuredGuideBody` (lead + points + lookFor).
 * - `StudioGuideEntry.body` is now `string | StructuredGuideBody` to let the
 *   shared `GuideCardContent` renderer handle both surfaces uniformly.
 * - `resolveStudioGuideContent` gains an `activeSection` parameter at priority 4.
 *
 * To add a new entry: add a key to StudioGuideKey, write its content object,
 * and the overlay will pick it up automatically via resolveStudioGuideContent().
 */

import type { StructuredGuideBody } from './guideContentTypes';

export interface StudioGuideEntry {
  heading: string;
  body: string | StructuredGuideBody;
  learnMoreUrl?: string;
}

// ---------------------------------------------------------------------------
// Key type — single source of truth for valid registry keys
// ---------------------------------------------------------------------------

export type StudioGuideKey =
  // Wave 1 — Overview, deep-settings, timeline
  | 'studio-overview'
  | 'deep-settings-body-form'
  | 'deep-settings-eom'
  | 'deep-settings-scoring-emphasis'
  | 'phase-timeline'
  | 'phase-selected'
  // Wave 2 — Per-tool guidance
  | 'tool-body-region'
  | 'tool-custom-goals'
  | 'tool-distance'
  | 'tool-movement'
  | 'tool-axis'
  | 'tool-timing'
  | 'tool-joint-angle'
  | 'tool-scoring-emphasis'
  // Wave 2 — Per-field micro-guidance
  | 'field-body-region-importance'
  | 'field-goal-landmark-picker'
  | 'field-phase-selector'
  | 'field-annotation-description'
  | 'field-scoring-studio-panel'
  // Fallback
  | 'default';

// ---------------------------------------------------------------------------
// Content registry
// ---------------------------------------------------------------------------

export const STUDIO_GUIDE_CONTENT: Record<StudioGuideKey, StudioGuideEntry> = {

  // ── Wave 1: Overview ──────────────────────────────────────────────────────

  'studio-overview': {
    heading: 'Coaching Studio',
    body: {
      lead: 'Annotate your reference video to define what the AI watches — and how much each part matters.',
      points: [
        'Focus panel: mark important body regions.',
        'Custom Goals panel: set movement checkpoints.',
        'Scoring section: configure phase weights and emphasis.',
      ],
      lookFor: 'The annotation tools panel on the left side of the canvas.',
    },
  },

  // ── Wave 1: Deep-settings states ──────────────────────────────────────────

  'deep-settings-body-form': {
    heading: 'Body Form Baseline',
    body: {
      lead: 'Compares joint angles and bone directions across your athlete\'s whole body, frame by frame.',
      points: [
        'The broadest quality signal — catches posture errors without explicit annotation.',
        'Higher weight amplifies differences your annotations don\'t specifically cover.',
        'Body Form is always active; the weight controls its share relative to your goals.',
      ],
      lookFor: 'The Body Form weight slider in the Scoring Studio panel.',
    },
  },

  'deep-settings-eom': {
    heading: 'Economy of Motion',
    body: {
      lead: 'Measures how efficiently an athlete moves — smooth, direct paths score higher.',
      points: [
        'Checks how the body gets there, not where it ends up.',
        'Use higher weight for skills where smooth execution matters (gymnastics, dance).',
        'This weight is separate from Body Form and Goal scoring, applied per phase.',
      ],
      lookFor: 'The Economy of Motion weight slider in the Scoring Studio panel.',
    },
  },

  'deep-settings-scoring-emphasis': {
    heading: 'Scoring Emphasis',
    body: {
      lead: 'Shifts the balance between your Custom Goal checkpoints and the Body Form baseline.',
      points: [
        '"Prioritize My Goals" raises the weight of explicit pass/fail checkpoints.',
        '"Prioritize Overall Form" increases the contribution of full-body posture scoring.',
        '"Balanced" splits the influence evenly between goals and form.',
      ],
      lookFor: 'The Scoring Emphasis selector in the Scoring Studio panel.',
    },
  },

  // ── Wave 1: Phase timeline states ─────────────────────────────────────────

  'phase-timeline': {
    heading: 'Phase Timeline',
    body: {
      lead: 'Phases divide your movement into named segments — each scored independently.',
      points: [
        'Athletes see exactly where their technique holds up and where it breaks down.',
        'Click a phase bar to select it and configure its scoring weight.',
        'Annotations can apply to the whole video or only a specific phase.',
      ],
      lookFor: 'The phase timeline bar above the video canvas.',
    },
  },

  'phase-selected': {
    heading: 'Phase Settings',
    body: {
      lead: 'Each phase contributes to the overall score based on its weight.',
      points: [
        'Weight 2 means this phase counts twice as much as a phase with weight 1.',
        'Speed intent per phase: "Same as reference" scores tempo, "Faster is better" / "Slower is better" reward one direction, and "Don\'t score speed here" ignores tempo.',
        'Adjust weights to emphasise the most important part of the movement.',
      ],
      lookFor: 'The phase settings panel that appears when a phase is selected.',
    },
  },

  // ── Wave 2: Per-tool guidance ─────────────────────────────────────────────

  'tool-custom-goals': {
    heading: 'Custom Goals',
    body: {
      lead: 'Define specific, measurable checkpoints the AI must verify — beyond overall body-shape matching.',
      points: [
        'Each goal type measures one thing: distance, movement path, axis/orientation, timing, or joint angle.',
        'Goals are scored per phase and blend into the phase score alongside body-form and economy.',
        'Mark a goal as a gate (shield icon) to zero the whole score when it fails — use sparingly for disqualifying faults.',
      ],
      lookFor: 'The Custom Goals panel and its goal-type tools.',
    },
  },

  'tool-body-region': {
    heading: 'Body Region Focus',
    body: {
      lead: 'Draw a region over a body zone to tell the AI where to focus most closely.',
      points: [
        'Set importance to Critical or High for regions where errors are disqualifying.',
        'By default, only annotated body parts are scored — unannotated areas are excluded (weight 0). Use the Global Body Match slider to include the whole body.',
        'Each annotation can apply to the whole video or a single phase.',
      ],
      lookFor: 'The region you\'re drawing on the video canvas.',
    },
  },

  'tool-distance': {
    heading: 'How far apart two body points are',
    body: {
      // Travel PR-B item 3 (docs/coach_input/TRAVEL_DISTANCE_BODY_HEIGHT_PLAN.md
      // §7.4 footnote): this used to claim an unnamed "body size" the check
      // was relative to. It is measured once, at a single moment, compared to
      // how tall the person's upper body looks in their own video — not over
      // time, and not the person's absolute height.
      lead: 'Checks how close together, or far apart, two points on the body are at one moment in the movement.',
      points: [
        'Example: "Both hands within 30 cm at the top of the movement."',
        'Measured against how tall the upper body looks in the video, not a fixed distance, so a taller and a shorter person can be compared.',
        'Pick the two points, then set the target and how strict to be.',
      ],
      lookFor: 'The point selector and distance target controls in the inspector.',
    },
  },

  'tool-movement': {
    heading: 'Movement Goal',
    body: {
      // Travel PR-B item 3: this used to describe recording an ideal path
      // and comparing its shape — a control that was never actually wired
      // up for movement goals. It now describes the travel-distance check
      // that ships in this panel (TRAVEL_PR_A_CONTRACT.md).
      lead: 'Checks how far a point travels, as a share of how tall the person looks in their own video.',
      points: [
        'Example: "The lead foot should travel at least as far as it does in my demo."',
        'Measure your own demo and use it as the target, or type the share yourself.',
        'Works best for a clear, sizeable movement — see the panel for when it will not tell you much.',
      ],
      lookFor: 'The travel-distance controls in the inspector.',
    },
  },

  'tool-axis': {
    heading: 'Axis Alignment Goal',
    body: {
      lead: 'Checks whether two landmarks (or anchor points) stay within an angular tolerance.',
      points: [
        'Example: "Shoulder–hip axis stays within 10° of vertical during descent."',
        'Mark as a gate if failure should zero the entire phase score.',
        'Supports arbitrary anchor points between joints using barycentric coordinates.',
      ],
      lookFor: 'The axis line drawn on the canvas and the angle tolerance controls.',
    },
  },

  'tool-timing': {
    heading: 'Timing Sequence Goal',
    body: {
      lead: 'Checks whether two body events happen in the correct order and within a timing window.',
      points: [
        'Example: "Front foot contacts ground BEFORE lead arm reaches extension."',
        'Set the allowed time gap between the two events.',
        'A gate on this goal zeros the phase score if timing is violated.',
      ],
      lookFor: 'The two landmark pickers and timing window controls in the inspector.',
    },
  },

  'tool-joint-angle': {
    heading: 'Joint Angle Goal',
    body: {
      lead: 'Checks whether the angle at a joint stays within a target range at a key moment.',
      points: [
        'Example: "Knee angle between 85°–95° at the bottom position."',
        'Pick three landmarks (proximal, vertex, distal) to define the angle.',
        'Most precise tool for checking depth, extension, and flexion.',
      ],
      lookFor: 'The three-landmark picker and angle range controls in the inspector.',
    },
  },

  'tool-scoring-emphasis': {
    heading: 'Scoring Emphasis',
    body: {
      lead: 'Adjust the balance between your Custom Goal annotations and the Body Form baseline.',
      points: [
        'These weights apply at the template level — every athlete evaluated against this template.',
        'Economy of Motion weight controls how much movement efficiency contributes.',
        'Per-phase timing weights are set in the Phase Settings section.',
      ],
      lookFor: 'The Scoring Studio panel (step 4 in the progress indicator).',
    },
  },

  // ── Wave 2: Per-field micro-guidance ──────────────────────────────────────

  'field-body-region-importance': {
    heading: 'Importance Level',
    body: {
      lead: 'Controls how much this region\'s score affects the phase total.',
      points: [
        'Critical (4×) and High (2.5×) regions dominate the score.',
        'Medium (1×) is neutral; Low (0.5×) still counts more than an unannotated part, which scores 0 by default.',
        'Most templates work well with one or two Critical regions and a few High regions.',
      ],
      lookFor: 'The importance selector (Critical / High / Medium / Low) in the region panel.',
    },
  },

  'field-goal-landmark-picker': {
    heading: 'Landmark Picker',
    body: {
      lead: 'Click a body joint on the video to select a landmark for your goal.',
      points: [
        'Filled circles = high visibility; dimmer circles = low-confidence detection.',
        'Functional landmarks (Lead / Trail side) resolve at evaluation time based on athlete\'s dominant side.',
        'Use functional landmarks for sport-specific cues that depend on handedness or lead leg.',
      ],
      lookFor: 'The joint overlay on the video canvas — click to select.',
    },
  },

  'field-phase-selector': {
    heading: 'Phase Scope',
    body: {
      lead: 'Choose whether this annotation applies to the entire video or just one phase.',
      points: [
        '"All frames" — for postural cues that should hold throughout the movement.',
        '"Current phase" — for checkpoints that only matter at a specific moment.',
        '"Frame range" — for explicit start/end frames if phases don\'t align.',
      ],
      lookFor: 'The phase scope dropdown above the annotation details.',
    },
  },

  'field-annotation-description': {
    heading: 'Annotation Label',
    body: {
      lead: 'This label appears in the athlete\'s results and the AI coaching feedback.',
      points: [
        'Write it as a coaching cue: "Front knee over toe", "Elbows tucked at lockout".',
        'Clear, coach-voiced descriptions produce better AI-generated tips.',
        'Avoid technical labels like "annotation_1" — they appear verbatim in athlete feedback.',
      ],
      lookFor: 'The label text field in the annotation panel.',
    },
  },

  'field-scoring-studio-panel': {
    heading: 'Scoring Controls',
    body: {
      lead: 'Full Body Form, Economy of Motion, and phase weight controls live in the Scoring Studio.',
      points: [
        'Continue to step 4 (Scoring Studio) after finishing your annotations here.',
        'You can return to add or edit annotations at any time.',
        'Click step 3 in the progress indicator at the top to return here.',
      ],
      lookFor: 'Step 4 in the progress indicator at the top of the page.',
    },
  },

  // ── Fallback ──────────────────────────────────────────────────────────────

  default: {
    heading: 'Coaching Studio Guide',
    body:
      'This panel shows contextual guidance as you work. ' +
      'Expand a tool panel on the left to see what it does. ' +
      'Select a phase in the timeline to see phase-specific tips. ' +
      'You can dismiss this guide at any time using the × button. ' +
      'It will reappear next time you open the Coaching Studio, unless you dismiss it ' +
      'from the Guide button in the header.',
  },
};

// ---------------------------------------------------------------------------
// Field ID → guide key mapping (Wave 2 Drawer-Field wiring)
// ---------------------------------------------------------------------------

/**
 * Maps a FIELD_IDS value to its corresponding studio guide key.
 * The overlay looks up the focusedField in this map to show micro-guidance.
 */
export const FIELD_ID_TO_GUIDE_KEY: Record<string, StudioGuideKey> = {
  'body-region-importance': 'field-body-region-importance',
  'goal-landmark-picker': 'field-goal-landmark-picker',
  'phase-selector': 'field-phase-selector',
  'annotation-description': 'field-annotation-description',
  'scoring-studio-panel': 'field-scoring-studio-panel',
};

// ---------------------------------------------------------------------------
// Tool name → guide key mapping (Wave 2 Tool-Selected wiring)
// ---------------------------------------------------------------------------

/**
 * Maps the `selectedTool` value (reported via `useAdaptiveGuide().report(...)`)
 * to a studio guide key. The overlay looks up the active tool in this map.
 */
export const TOOL_TO_GUIDE_KEY: Record<string, StudioGuideKey> = {
  'body-region': 'tool-body-region',
  'distance': 'tool-distance',
  'movement': 'tool-movement',
  'axis': 'tool-axis',
  'timing': 'tool-timing',
  'joint-angle': 'tool-joint-angle',
  'scoring-emphasis': 'tool-scoring-emphasis',
  // Alias: RegionAnnotator reports the panel id directly
  focus: 'tool-body-region',
  'custom-goals': 'tool-body-region', // group-level fallback
  scoring: 'tool-scoring-emphasis',
};

// ---------------------------------------------------------------------------
// Section → guide key mapping (B-2026-0088 Phase A2 — studio section spy)
// ---------------------------------------------------------------------------

/**
 * Maps `data-guide-section` attribute values added to RegionAnnotator panel
 * containers (Phase A2) to registry keys.
 */
export const STUDIO_SECTION_TO_KEY: Record<string, StudioGuideKey> = {
  'studio-panel-body-region':    'tool-body-region',
  'studio-panel-custom-goals':   'tool-custom-goals',
  'studio-panel-scoring':        'deep-settings-scoring-emphasis',
  'studio-panel-phase-timeline': 'phase-timeline',
};

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Resolves the correct guide entry from the current context state.
 *
 * Priority (highest first):
 *   1. focusedField   — field-level micro-guidance (Wave 2)
 *   2. selectedTool   — tool-selected guidance (Wave 2)
 *   3. activePanel    — deep-settings guidance (Wave 1)
 *   4. activeSection  — scroll-spy / visible panel (B-2026-0088 Phase A2)
 *   5. activePhase    — phase-selected guidance (Wave 1)
 *   6. null           — fall back to 'studio-overview'
 */
export function resolveStudioGuideContent(
  focusedField: string | null,
  selectedTool: string | null,
  activePanel: string | null,
  activePhase: number | null,
  activeSection?: string | null,
): StudioGuideEntry {
  // 1. Field-level micro-guidance
  if (focusedField) {
    const fieldKey = FIELD_ID_TO_GUIDE_KEY[focusedField];
    if (fieldKey && fieldKey in STUDIO_GUIDE_CONTENT) {
      return STUDIO_GUIDE_CONTENT[fieldKey];
    }
  }

  // 2. Tool-selected guidance
  if (selectedTool) {
    const toolKey = TOOL_TO_GUIDE_KEY[selectedTool];
    if (toolKey && toolKey in STUDIO_GUIDE_CONTENT) {
      return STUDIO_GUIDE_CONTENT[toolKey];
    }
  }

  // 3. Deep-settings panel guidance (activePanel carries the inspector section id)
  if (activePanel) {
    if (activePanel === 'scoring' || activePanel === STUDIO_GUIDE_CONTENT['deep-settings-scoring-emphasis'].heading) {
      return STUDIO_GUIDE_CONTENT['deep-settings-scoring-emphasis'];
    }
    if (activePanel === 'body-form') {
      return STUDIO_GUIDE_CONTENT['deep-settings-body-form'];
    }
    if (activePanel === 'eom') {
      return STUDIO_GUIDE_CONTENT['deep-settings-eom'];
    }
    // Generic activePanel → try direct key lookup
    const directKey = activePanel as StudioGuideKey;
    if (directKey in STUDIO_GUIDE_CONTENT) {
      return STUDIO_GUIDE_CONTENT[directKey];
    }
  }

  // 4. Section spy — which studio panel is currently in view (Phase A2)
  if (activeSection) {
    const sectionKey = STUDIO_SECTION_TO_KEY[activeSection];
    if (sectionKey && sectionKey in STUDIO_GUIDE_CONTENT) {
      return STUDIO_GUIDE_CONTENT[sectionKey];
    }
    // Try direct key lookup for section values that match registry keys
    const directKey = activeSection as StudioGuideKey;
    if (directKey in STUDIO_GUIDE_CONTENT) {
      return STUDIO_GUIDE_CONTENT[directKey];
    }
  }

  // 5. Phase selected
  if (activePhase !== null) {
    return STUDIO_GUIDE_CONTENT['phase-selected'];
  }

  // 6. Default overview
  return STUDIO_GUIDE_CONTENT['studio-overview'];
}
